import { useIntl } from 'react-intl';
import { User } from '@rossum/api-client/users';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { useUnpaginatedUsers } from '../../../../business/users/useUnpaginatedUsers';
import { boldText, white } from '../../../../lib/formaterValues';

export type ToggleAllDialogAction =
  | 'disableUsersOnQueue'
  | 'enableUsersOnQueue'
  | undefined;
type ToggleAllDialogProps = {
  action: ToggleAllDialogAction;
  queueName: string;
  count: number;
  onClose: () => void;
  onSubmit: (filteredUsers: User[]) => void;
  searchValue: string | undefined;
};
export const ToggleAllDialog = ({
  action,
  queueName,
  count,
  onClose,
  onSubmit,
  searchValue,
}: ToggleAllDialogProps) => {
  const intl = useIntl();
  const open = !!action;

  const { data: filteredUsers } = useUnpaginatedUsers(
    {
      deleted: false,
      search: searchValue,
    },
    { enabled: open }
  );

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: { width: 480, minHeight: 220, position: 'fixed' },
        elevation: 2,
      }}
      onClose={onClose}
      sx={{ transition: 'smooth' }}
    >
      {action ? (
        <Stack>
          <DialogTitle
            title={intl.formatMessage({
              id: `components.modal.${action}.title`,
            })}
            onClose={onClose}
          />
          <DialogContent sx={{ mx: 4, mt: 4, p: 0 }}>
            <Typography variant="body1">
              {intl.formatMessage(
                {
                  id: `components.modal.${action}.text`,
                },
                { bold: boldText, white, count, queueName }
              )}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ pb: 4, pt: 2.5, mr: 4, pr: 0 }}>
            <Button
              variant="outlined"
              size="medium"
              color="secondary"
              onClick={onClose}
              data-cy="modal-cancel-button"
            >
              {intl.formatMessage({
                id: `components.modal.${action}.cancel`,
              })}
            </Button>
            <Button
              variant="contained"
              size="medium"
              color="primary"
              disabled={!filteredUsers}
              startIcon={
                filteredUsers ? undefined : <CircularProgress size={20} />
              }
              onClick={() => onSubmit(filteredUsers ?? [])}
              data-cy="modal-confirm-button"
            >
              {intl.formatMessage({
                id: `components.modal.${action}.confirm`,
              })}
            </Button>
          </DialogActions>
        </Stack>
      ) : null}
    </Dialog>
  );
};
